import { thousandsSeparators } from "../../../../config/utils";
import {
    buildAxisLabelHtml,
    getCountChartType,
    getGroupColor,
    getGroupedAssetFilterKey,
    getLegendSort,
    getMeasureOption,
    isCostMeasureOption,
    isGroupedCostChart,
    sortGroupByOptions,
    supportsMeasureToggle
} from "./groupedAssetChart";
import { removeAxisLabelTitles } from "./weightedAverage";
import { applyTopNSeries, buildTooltipLines, formatCostInMillions, formatValueAssetCount, sortChartPoints, spreadStackedSegments } from "./utils";

export const HORIZONTAL_CHART_KEY = "Horizontal_Chart";
export const HORIZONTAL_CHART_PARAM_KEY = "horizontal_chart";

export const HORIZONTAL_DEFAULT_TYPE = "region";
export const HORIZONTAL_DEFAULT_GROUP_BY = "asset_condition";
export const HORIZONTAL_DEFAULT_SORT = { sortBy: "value", sortOrder: "desc" };

export const HORIZONTAL_TYPE_OPTIONS = [
    { value: "building", label: "Building", filterKey: "buildings.name" },
    { value: "building_type", label: "Building Type", filterKey: "main_building_types.name" },
    { value: "site", label: "Site", filterKey: "sites.name" },
    { value: "region", label: "Region", filterKey: "regions.name" },
    { value: "project", label: "Project", filterKey: null }
];

export const HORIZONTAL_GROUP_BY_OPTIONS = sortGroupByOptions([
    { value: "asset_condition", label: "Condition" },
    { value: "asset_end_of_life", label: "End of Service Life" },
    { value: "asset_age_chart", label: "Age" },
    { value: "uniformat_level_1s", label: "Uniformat Level 1" },
    { value: "uniformat_level_2s", label: "Uniformat Level 2" },
    { value: "uniformat_level_3s", label: "Uniformat Level 3" },
    { value: "uniformat_level_4s", label: "Uniformat Level 4" },
    { value: "uniformat_level_5s", label: "Uniformat Level 5" },
    { value: "uniformat_level_6s", label: "Uniformat Level 6" }
]);

// A cost grouping shows under its count twin's label
const getHorizontalGroupByOption = groupBy => {
    const baseValue = getMeasureOption(groupBy, "count") || groupBy;
    return HORIZONTAL_GROUP_BY_OPTIONS.find(option => option.value === baseValue) || HORIZONTAL_GROUP_BY_OPTIONS[0];
};

export const getHorizontalGroupByLabel = groupBy => getHorizontalGroupByOption(groupBy).label;

const getHorizontalTypeOption = type => HORIZONTAL_TYPE_OPTIONS.find(option => option.value === type) || HORIZONTAL_TYPE_OPTIONS[0];

export const getHorizontalTypeLabel = type => getHorizontalTypeOption(type).label;

export const getHorizontalTypeFilterKey = type => getHorizontalTypeOption(type).filterKey;

export const getHorizontalGroupBy = (chartParams = {}) => chartParams?.horizontal_group_by || HORIZONTAL_DEFAULT_GROUP_BY;

export const getHorizontalType = (chartParams = {}) => chartParams?.horizontal_chart_type || HORIZONTAL_DEFAULT_TYPE;

export const isHorizontalCostChart = (chartParams = {}) => isGroupedCostChart(getHorizontalGroupBy(chartParams));

export const getHorizontalSelectedGroupBy = (chartParams = {}) => getHorizontalGroupByOption(getHorizontalGroupBy(chartParams));

export const withHorizontalActiveMeasure = (chartParams = {}, optionValue) => {
    const activeValue = getHorizontalGroupBy(chartParams);
    return (isCostMeasureOption(activeValue) && getMeasureOption(optionValue, "cost")) || optionValue;
};

export const getHorizontalMeasureSetting = (chartParams = {}, handleHorizontalGroupBy) => {
    const activeValue = getHorizontalGroupBy(chartParams);
    if (!handleHorizontalGroupBy || !supportsMeasureToggle(activeValue)) return null;
    return {
        valueMode: isCostMeasureOption(activeValue) ? "amount" : "count",
        onValueModeChange: mode => {
            const target = getMeasureOption(activeValue, mode === "amount" ? "cost" : "count");
            if (target && target !== activeValue) handleHorizontalGroupBy(target);
        }
    };
};

export const getHorizontalChartHeading = (chartParams = {}) =>
    `${getHorizontalGroupByLabel(getHorizontalGroupBy(chartParams))} by ${getHorizontalTypeLabel(getHorizontalType(chartParams))}`;

export const getHorizontalChartParams = (chartParams = {}, scopeParams = {}) => ({
    ...scopeParams,
    // Always the count grouping: the Value measure reads the `value` key of this same response
    chart_type: getCountChartType(getHorizontalGroupBy(chartParams)),
    chart_sort_by: chartParams?.horizontal_chart_sort_by || HORIZONTAL_DEFAULT_SORT.sortBy,
    chart_sort_order: chartParams?.horizontal_chart_sort_order || HORIZONTAL_DEFAULT_SORT.sortOrder,
    horizontal_chart_type: getHorizontalType(chartParams),
    list: chartParams?.list || {},
    filters: chartParams?.filters || null,
    sfci_chart_sort_by: chartParams?.sfci_chart_sort_by || "value",
    sfci_chart_sort_order: chartParams?.sfci_chart_sort_order || "desc",
    user_id: localStorage.getItem("userId")
});

const toCount = value => {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
};

const findHorizontalRows = payload => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    const namedRows = payload["horizontal_chart"].find(value => Array.isArray(value));
    if (namedRows) return namedRows;
    return Object.values(payload).find(value => Array.isArray(value) && (!value.length || typeof value[0] === "object")) || [];
};

const getStackName = stack => stack?.name;

const getStackColor = stack => stack?.color || null;

const getStackId = stack => stack?.id ?? stack?.condition?.id ?? stack?.condtion?.id ?? null;

export const normalizeHorizontalChartRows = payload =>
    findHorizontalRows(payload).map(row => ({
        name: row?.name || row?.label || row?.site || row?.building || row?.region || "Unknown",
        entityId: row?.id ?? row?.entity_id ?? null,
        data: (Array.isArray(row?.data) ? row.data : [row]).map(stack => ({
            name: getStackName(stack),
            y: toCount(stack?.asset_count ?? stack?.value),
            value: toCount(stack?.value),
            assetCount: toCount(stack?.asset_count ?? stack?.count),
            // The assets that actually carry a value — what the Value measure counts
            valueAssetCount: toCount(stack?.value_asset_count ?? stack?.asset_count ?? stack?.count),
            color: getStackColor(stack),
            groupId: getStackId(stack)
        }))
    }));

export const getHorizontalChartRows = chartResponse => (Array.isArray(chartResponse?.horizontal_chart) ? chartResponse.horizontal_chart : []);

export const getHorizontalChartTotal = rows =>
    (rows || []).reduce((total, row) => total + (row.data || []).reduce((sum, stack) => sum + (stack.y || 0), 0), 0);

// On Value: the assets carrying a cost, and every asset behind the bars, shown as a ratio above the chart
export const getHorizontalAssetCountTotals = rows =>
    (rows || []).reduce(
        (totals, row) =>
            (row?.data || []).reduce(
                (rowTotals, stack) => ({
                    assetCount: rowTotals.assetCount + toCount(stack?.assetCount),
                    totalAssetCount: rowTotals.totalAssetCount + toCount(stack?.totalAssetCount)
                }),
                totals
            ),
        { assetCount: 0, totalAssetCount: 0 }
    );

const getHorizontalRowTotal = row => (row?.data || []).reduce((total, stack) => total + toCount(stack?.y), 0);

export const sortHorizontalRows = (rows = [], sortBy = HORIZONTAL_DEFAULT_SORT.sortBy, sortOrder = HORIZONTAL_DEFAULT_SORT.sortOrder) => {
    const direction = sortOrder === "asc" ? 1 : -1;
    return [...(rows || [])].sort((first, second) =>
        sortBy === "name"
            ? direction * String(first?.name ?? "").localeCompare(String(second?.name ?? ""), undefined, { numeric: true, sensitivity: "base" })
            : direction * (getHorizontalRowTotal(first) - getHorizontalRowTotal(second))
    );
};

// Value reads the `value` key of the rows the Count call already returned, so the sort sees the measure on screen
const applyHorizontalMeasure = (rows, isCost) =>
    !isCost
        ? rows || []
        : (rows || []).map(row => ({
              ...row,
              data: (row.data || []).map(stack => ({
                  ...stack,
                  y: stack.value || 0,
                  assetCount: stack.valueAssetCount ?? stack.assetCount ?? 0,
                  totalAssetCount: stack.assetCount ?? 0
              }))
          }));

export const getSortedHorizontalRows = (rows, chartParams = {}) =>
    sortHorizontalRows(
        applyHorizontalMeasure(rows, isHorizontalCostChart(chartParams)),
        chartParams?.horizontal_chart_sort_by,
        chartParams?.horizontal_chart_sort_order
    );

export const getHorizontalLegendSort = (chartParams = {}) =>
    getLegendSort(chartParams?.horizontal_chart_sort_by, chartParams?.horizontal_chart_sort_order === "asc");

export const buildHorizontalChartSeries = rows => {
    const seriesMap = new Map();
    (rows || []).forEach((row, rowIndex) => {
        (row.data || []).forEach(stack => {
            const key = stack.name || "Unknown";
            if (!seriesMap.has(key)) {
                seriesMap.set(key, { name: key, color: stack.color || null, data: new Array(rows.length).fill(null) });
            }
            const entry = seriesMap.get(key);
            entry.data[rowIndex] = {
                y: stack.y || null,
                assetCount: stack.assetCount || 0,
                // Every asset in the segment, against which assetCount is the with-cost slice on Value
                totalAssetCount: stack.totalAssetCount || 0,
                groupName: key,
                groupId: stack.groupId ?? null,
                categoryName: row.name,
                categoryEntityId: row.entityId ?? null,
                ...(stack.isOthers ? { isOthers: true, othersMembers: stack.othersMembers || [] } : {})
            };
            if (!entry.color && stack.color) entry.color = stack.color;
        });
    });
    const orderedSeries = Array.from(seriesMap.values());
    return orderedSeries.map((series, index) => ({
        ...series,
        color: series.color || getGroupColor(index),
        data: series.data.map(
            (point, rowIndex) =>
                point || {
                    y: null,
                    groupName: series.name,
                    groupId: null,
                    categoryName: rows[rowIndex]?.name,
                    categoryEntityId: rows[rowIndex]?.entityId ?? null
                }
        )
    }));
};

export const getHorizontalGroupSeries = (rows, topN) => applyTopNSeries(buildHorizontalChartSeries(rows || []), topN);
export const getHorizontalGroupLegendValues = (rows, topN, legendSort = getLegendSort()) =>
    sortChartPoints(
        getHorizontalGroupSeries(rows, topN).map(series => {
            const y = (series.data || []).reduce((total, point) => total + toCount(point?.y), 0);
            return {
                name: series.name,
                displayName: series.name,
                y,
                valueSum: y,
                assetCount: (series.data || []).reduce((total, point) => total + toCount(point?.assetCount), 0),
                color: series.color,
                ...(series.isOthers ? { isOthers: true, othersMembers: series.othersMembers || [] } : {})
            };
        }),
        legendSort?.sortBy,
        legendSort?.sortAsc
    );

export const formatHorizontalCostValue = formatCostInMillions;

export const HORIZONTAL_AXIS_LABEL_MAX_CHARS = 12;
const HORIZONTAL_CHART_MARGIN_LEFT = 90;
const AXIS_LABEL_GUTTER = 10;

const STACK_LABEL_GAP = 5;
const MIN_SEGMENT_LENGTH = 2;

export const positionHorizontalStackLabels = chart => {
    const yAxis = chart?.yAxis?.[0];
    const stacks = yAxis?.stacking?.stacks;
    if (!stacks) return;
    const barEnds = new Map();
    (chart.series || []).forEach(series => {
        if (!series.visible) return;
        (series.points || []).forEach(point => {
            if (point.y == null || !point.shapeArgs) return;
            const end = yAxis.len - point.shapeArgs.y;
            if (end > (barEnds.get(point.x) || 0)) barEnds.set(point.x, end);
        });
    });
    Object.values(stacks).forEach(stackCollection => {
        Object.values(stackCollection || {}).forEach(stackItem => {
            const label = stackItem?.label;
            const barEnd = barEnds.get(stackItem?.x);
            if (!label?.element || barEnd == null) return;
            const labelWidth = label.getBBox().width;
            if (!labelWidth) return;
            const outsideX = barEnd + STACK_LABEL_GAP;
            const fitsOutside = outsideX + labelWidth + STACK_LABEL_GAP <= chart.plotWidth;
            label.attr({ x: fitsOutside ? outsideX : Math.max(barEnd - labelWidth - STACK_LABEL_GAP, 0) });
            label.css({
                color: fitsOutside ? "gray" : "#FFFFFF",
                textOutline: fitsOutside ? "none" : "1px rgba(0, 0, 0, 0.35)"
            });
        });
    });
};

const endsTheBar = (point, chart) => {
    const ownEnd = point?.shapeArgs?.y;
    if (ownEnd == null) return false;
    return !(chart?.series || []).some(
        series =>
            series.visible &&
            (series.points || []).some(
                other =>
                    other !== point &&
                    other.x === point.x &&
                    other.y &&
                    other.shapeArgs &&
                    (other.shapeArgs.y < ownEnd || (other.shapeArgs.y === ownEnd && series.index > point.series.index))
            )
    );
};

const hidePointsOutsideWindow = chart => {
    const extremes = chart?.xAxis?.[0]?.getExtremes?.() || {};
    const windowMin = (extremes.min ?? 0) - 0.5;
    const windowMax = (extremes.max ?? 0) + 0.5;
    (chart.series || []).forEach(series =>
        (series.points || []).forEach(point => {
            if (!point?.graphic) return;
            if (point.x < windowMin || point.x > windowMax || !series.visible || point.visible === false) {
                point.graphic.hide();
            } else {
                point.graphic.show();
            }
        })
    );
};

export const buildHorizontalAssetChartOptions = (
    rows,
    {
        isCost = false,
        is3d = false,
        topN = null,
        maxVisibleBars = 5,
        showLegend = false,
        selectedCategory = null,
        showPointSelection = false,
        legendSort,
        onPointClick,
        onCategoryClick,
        onAxisLabelHover,
        onAxisLabelOut
    } = {}
) => {
    const categories = (rows || []).map(row => row.name);
    const builtSeries = getHorizontalGroupSeries(rows, topN, legendSort);
    const series = builtSeries.length ? builtSeries : [{ name: "Assets", data: [] }];
    const scrollEnabled = categories.length > maxVisibleBars;
    const formatValue = value => (isCost ? formatCostInMillions(value) : thousandsSeparators(value || 0));
    return {
        exporting: { enabled: false },
        credits: { enabled: false },
        chart: {
            name: "asset_horizontal_chart",
            type: "bar",
            ...(is3d ? {} : { zoomType: "xy" }),
            marginLeft: HORIZONTAL_CHART_MARGIN_LEFT,
            backgroundColor: "#FFFFFF",
            style: { fontFamily: "Poppins, sans-serif" },
            options3d: { enabled: is3d, alpha: 15, beta: 15, viewDistance: 25, depth: 95 },
            events: {
                render: function () {
                    removeAxisLabelTitles(this);
                    if (!is3d) {
                        spreadStackedSegments(this, MIN_SEGMENT_LENGTH);
                        positionHorizontalStackLabels(this);
                        return;
                    }
                    if (scrollEnabled) hidePointsOutsideWindow(this);
                }
            }
        },
        title: { text: "" },
        xAxis: {
            categories,
            title: { text: null },
            scrollbar: { enabled: scrollEnabled },
            min: 0,
            max: scrollEnabled ? maxVisibleBars : null,
            labels: {
                enabled: true,
                align: is3d ? "right" : "left",
                x: is3d ? -8 : -(HORIZONTAL_CHART_MARGIN_LEFT - AXIS_LABEL_GUTTER),
                ...(is3d ? { skew3d: false } : {}),
                style: {
                    cursor: "pointer",
                    width: `${HORIZONTAL_CHART_MARGIN_LEFT - AXIS_LABEL_GUTTER}px`,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis"
                },
                events: {
                    mouseover: function (event) {
                        onAxisLabelHover?.(this.axis.categories?.[this.pos], event?.currentTarget || event?.target);
                    },
                    mouseout: function () {
                        onAxisLabelOut?.();
                    },
                    click: function (event) {
                        onCategoryClick?.(this.axis.categories?.[this.pos] || "", this.pos, {
                            ...event,
                            chartAnchorElement: event?.currentTarget || event?.target,
                            chartContainerElement: this.chart?.container
                        });
                    }
                },
                formatter: item => {
                    const isSelected = selectedCategory != null && selectedCategory === item.value;
                    return buildAxisLabelHtml(item.value, {
                        color: isSelected ? "#007bff" : "black",
                        fontWeight: isSelected ? "bold" : "normal",
                        maxChars: HORIZONTAL_AXIS_LABEL_MAX_CHARS
                    });
                }
            }
        },
        yAxis: {
            min: 0,
            allowDecimals: false,
            title: { text: "" },
            stackLabels: {
                enabled: !is3d,
                crop: false,
                overflow: "allow",
                allowOverlap: true,
                style: { fontWeight: "bold", color: "gray" },
                formatter: function () {
                    if (this.total == null) return "";
                    return isCost ? formatHorizontalCostValue(this.total) : thousandsSeparators(this.total);
                }
            }
        },
        tooltip: {
            useHTML: true,
            formatter: function () {
                // The merged "Others" series carries no categoryName of its own — fall back to the axis category
                // On Value, the assets carrying a cost are shown against every asset in the segment
                return `<b>${this.point.categoryName || this.point.category || this.x}</b><br/>${buildTooltipLines(this.color, [
                    `${this.series.name}: ${formatValue(this.y)}`,
                    isCost && `Assets: ${formatValueAssetCount(this.point)}`,
                    "Click To View Details"
                ])}`;
            }
        },
        plotOptions: {
            series: {
                stacking: "normal",
                ...(is3d ? {} : { minPointLength: MIN_SEGMENT_LENGTH }),
                dataLabels: {
                    enabled: is3d,
                    // Anchors the label just past the end of the segment, which Highcharts then projects
                    inside: false,
                    crop: false,
                    overflow: "allow",
                    allowOverlap: true,
                    // A white halo keeps the grey readable where a label overlaps a bar or a gridline
                    style: { fontWeight: "bold", color: "gray", textOutline: "2px #FFFFFF" },
                    formatter: function () {
                        const point = this.point;
                        const total = point?.stackTotal ?? point?.y;
                        if (total == null || !endsTheBar(point, this.series?.chart)) return null;
                        // 3D graphics are not clipped to the scrolled category window, so out-of-window
                        // bars would otherwise keep their totals after hidePointsOutsideWindow hid them
                        const extremes = this.series?.chart?.xAxis?.[0]?.getExtremes?.();
                        if (extremes && (point.x < extremes.min || point.x > extremes.max)) return null;
                        return isCost ? formatHorizontalCostValue(total) : thousandsSeparators(total);
                    }
                },
                point: {
                    events: {
                        click: event => onPointClick?.(event)
                    }
                },
                allowPointSelect: true,
                cursor: "pointer",
                states: {
                    select: {
                        color: null,
                        borderWidth: showPointSelection ? 2 : null,
                        borderColor: showPointSelection ? "black" : null
                    }
                }
            }
        },
        // maxHeight caps the legend at ~4 rows of items; the rest pages through the built-in navigator
        legend: { enabled: showLegend, maxHeight: 90 },
        series
    };
};

export const getHorizontalChartPopupData = ({ heading, chartParams, point }) => {
    const groupBy = getHorizontalGroupBy(chartParams);
    const horizontalType = getHorizontalType(chartParams);
    return {
        heading,
        category: point?.categoryName || "",
        categoryLabel: getHorizontalTypeLabel(horizontalType),
        name: point?.groupName || "",
        entityId: point?.groupId ?? null,
        data: point?.y ?? 0,
        // Both counts ride along so a Value popup can split the segment by cost type
        assetCount: point?.assetCount ?? 0,
        totalAssetCount: point?.totalAssetCount ?? 0,
        type: HORIZONTAL_CHART_KEY,
        groupBy,
        groupLabel: getHorizontalGroupByLabel(groupBy),
        groupFilterKey: getGroupedAssetFilterKey(groupBy),
        groupIsCost: isGroupedCostChart(groupBy),
        horizontalType,
        horizontalFilterKey: getHorizontalTypeFilterKey(horizontalType)
    };
};

export const getHorizontalCategoryPopupData = ({ heading, chartParams, categoryName, rangeIndex, series }) => ({
    ...getHorizontalChartPopupData({ heading, chartParams, point: { categoryName } }),
    name: null,
    series: series || [],
    rangeIndex,
    isCategory: true
});
